import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";
import SectionTitle from "@/components/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";

const VALUES = [
  { num: "01", title: "Respect", desc: "De la mémoire, des croyances, des silences. Rien n'est imposé : tout est proposé." },
  { num: "02", title: "Discrétion", desc: "Confidentialité absolue à chaque étape. Nous travaillons dans la pudeur et l'attention." },
  { num: "03", title: "Transparence", desc: "Devis détaillé écrit, prix clairs, aucune surprise. Vous savez exactement ce que vous payez." },
  { num: "04", title: "Accompagnement personnalisé", desc: "Chaque famille est unique. Nous adaptons chaque détail, sans jamais imposer un modèle." },
];

export default function QuiSommesNousPage() {
  usePageMeta(
    "Qui sommes-nous — Entreprise familiale et indépendante",
    "Pompes Funèbres Lumière des Nations : entreprise familiale et indépendante à Baho. Nos valeurs : respect, discrétion, transparence, accompagnement personnalisé."
  );
  return (
    <div data-testid="qui-sommes-nous-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.hands} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Qui sommes-nous</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Une maison <em className="italic font-light">indépendante & familiale</em>, au service des Pyrénées-Orientales.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            Pompes Funèbres Lumière des Nations a été fondée pour offrir aux familles de Baho,
            Perpignan et alentours un accompagnement à la fois professionnel et profondément humain.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="container-soft grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="overline">Notre histoire</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              Une vocation : accompagner avec humanité.
            </h2>
            <div className="mt-8 space-y-6 text-neutral-700 text-lg leading-relaxed">
              <p>
                Notre maison est née d'un constat simple : dans les moments les plus difficiles,
                les familles ont besoin d'un interlocuteur calme, disponible et sincère. Pas d'un
                vendeur, pas d'un intermédiaire. Une présence.
              </p>
              <p>
                C'est cette conviction qui guide chacune de nos décisions. Indépendants et familiaux,
                nous ne rendons de comptes qu'aux familles que nous accompagnons. Notre engagement
                tient en quatre mots : <strong className="font-medium">respect, discrétion, transparence, accompagnement</strong>.
              </p>
              <p>
                Nous travaillons avec des artisans locaux — marbriers, fleuristes, officiants —
                tous choisis pour la qualité et la dignité de leur travail. Et nous restons joignables
                <strong className="font-medium"> 7 jours sur 7, 24 heures sur 24.</strong>
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-black/15 p-8 lg:p-10">
              <p className="overline">Notre badge</p>
              <h3 className="font-serif-display text-3xl mt-3 leading-tight">
                Entreprise indépendante <em className="italic">&</em> familiale
              </h3>
              <div className="hairline my-8" />
              <ul className="space-y-3 text-neutral-700">
                <li className="flex justify-between gap-4 pb-2 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Implantation</span>
                  <span>Baho (66540)</span>
                </li>
                <li className="flex justify-between gap-4 pb-2 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Disponibilité</span>
                  <span>7j/7 — 24h/24</span>
                </li>
                <li className="flex justify-between gap-4 pb-2 border-b border-black/10">
                  <span className="text-neutral-500 text-sm">Tarifs</span>
                  <span>Dès {SITE.priceFrom}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span className="text-neutral-500 text-sm">Engagement</span>
                  <span>Indépendance totale</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft">
          <SectionTitle
            overline="Nos valeurs"
            title="Quatre engagements, tenus à chaque instant."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 mt-16 border border-black/10">
            {VALUES.map((v) => (
              <article
                key={v.num}
                data-testid={`value-${v.num}`}
                className="bg-white p-10 hover:bg-neutral-50 transition-colors duration-500"
              >
                <div className="font-serif-display text-5xl text-black/30 leading-none">{v.num}</div>
                <h3 className="font-serif-display text-2xl mt-6">{v.title}</h3>
                <p className="mt-3 text-neutral-600 leading-relaxed">{v.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 text-center">
        <div className="container-soft">
          <SectionTitle
            overline="Faisons connaissance"
            title="Un appel, et nous serons là."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.phoneHref} data-testid="qsn-cta-call" className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300">
              <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
            </a>
            <Link to="/contact" data-testid="qsn-cta-contact" className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300">
              Nous écrire <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
